import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dynamic Font Sizing Hook with Calibration System
 * 
 * This hook automatically adjusts font sizes to prevent scrollbars while maximizing readability.
 * 
 * ## Why Large Initial Font Sizes Cause Layout Issues:
 * 
 * 1. **CSS Grid Overflow**: When the page loads, CSS Grid tries to fit content in columns
 * 2. **Initial Render**: Large fonts (26px+) can make even short text wider than 50vw
 * 3. **Browser Response**: Browser adds horizontal scrollbar to accommodate overflow
 * 4. **Late Correction**: Our calculations run after initial render, scrollbar persists
 * 
 * ## Calibration Guide:
 * 
 * **HEIGHT_BUFFER_PERCENT**: Controls how aggressively text fills vertical space
 * - Lower (5-10%): Text uses more space, might trigger scrollbars
 * - Higher (10-15%): More conservative, ensures no scrollbars
 * 
 * **FONT_SIZE_FACTORS**: Accounts for larger fonts being harder to predict
 * - Uses indices from FONT_SIZES array (0=smallest, 3=largest)
 * - Larger fonts need higher factors (1.2+) due to:
 *   - More spacing variations
 *   - Greater visual impact of overflow
 *   - Less predictable line breaking
 * 
 * **SAFE_INITIAL_INDEX**: Prevents layout issues on page load
 * - Use index 0-1 for safe loading (18-22px)
 * - Avoid index 2+ as initial (24px+) to prevent scrollbars
 * 
 * ## Fine-tuning Tips:
 * 
 * - **Text shrinks too late**: Decrease HEIGHT_BUFFER_PERCENT
 * - **Horizontal scrollbars**: Increase WIDTH_PADDING
 * - **Vertical scrollbars**: Increase HEIGHT_PADDING or HEIGHT_BUFFER_PERCENT
 * - **Initial layout jumps**: Lower SAFE_INITIAL_INDEX
 */

// Font size variants in pixels
const FONT_SIZES = [
  16, // sm (default/minimum)
  20, // base 
  24, // lg
  28, // xl
] as const;

// Calibration constants for fine-tuning the dynamic font sizing behavior
const CALIBRATION = {
  // Height buffer: Percentage of available height to reserve as buffer
  // Lower values = more aggressive scaling (text uses more space)
  // Higher values = more conservative scaling (more breathing room)
  HEIGHT_BUFFER_PERCENT: 8, // 8% buffer (was 5% before)

  // Width padding: Additional pixels to account for container padding/margins
  // Increase if text appears to overflow horizontally
  WIDTH_PADDING: 32, // 40px total (20px each side, was 32px)

  // Height padding: Additional pixels to account for buttons, controls, etc.
  // Increase if text appears to overflow vertically
  HEIGHT_PADDING: 80, // 90px total (was 80px)

  // Safe initial font: Font size to use for empty/initial state
  // Should be safe to render without causing layout issues
  SAFE_INITIAL_INDEX: 3, // Index in FONT_SIZES array (22px instead of largest)

  // Font size scaling factors based on font size index in FONT_SIZES array
  // Higher indices (larger fonts) need more conservative calculations
  FONT_SIZE_FACTORS: [
    1.0,   // Index 0 (18px) - Baseline factor
    1.0,   // Index 1 (22px) - 10% more conservative
    1.0,  // Index 2 (24px) - 15% more conservative  
    1.1,   // Index 3 (26px) - 20% more conservative
  ] as const
} as const;


type FontSize = typeof FONT_SIZES[number];

interface TextMeasurement {
  width: number;
  height: number;
}

interface BoxDimensions {
  width: number;
  height: number;
}

// Cache for text measurements to avoid repeated calculations
// Using a simple Map with a maximum size to prevent memory leaks
const measurementCache = new Map<string, TextMeasurement>();
const MAX_CACHE_SIZE = 50; // Reduced cache size since we're now using content-based keys

/**
 * Simple hash function for creating consistent cache keys from text content
 */
function hashText(text: string): string {
  let hash = 0;
  if (text.length === 0) return hash.toString();

  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString(36); // Convert to base36 for shorter string
}

function addToCache(key: string, value: TextMeasurement) {
  if (measurementCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry
    const firstKey = measurementCache.keys().next().value;
    if (firstKey) {
      measurementCache.delete(firstKey);
    }
  }
  measurementCache.set(key, value);
}

/**
 * Measures text dimensions for a given font size
 */
function measureText(text: string, fontSize: FontSize, maxWidth: number, isMarkdown: boolean = false): TextMeasurement {
  // Create cache key using hash of text content to ensure uniqueness while keeping keys manageable
  const textHash = hashText(text);
  const cacheKey = `${textHash}-${fontSize}-${maxWidth}-${isMarkdown}`;

  if (measurementCache.has(cacheKey)) {
    return measurementCache.get(cacheKey)!;
  }

  try {
    // Create a temporary canvas for text measurement
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      // Fallback calculation if canvas is not available
      return fallbackMeasurement(text, fontSize, maxWidth);
    }

    // Use the same font family as the textarea
    context.font = `${fontSize}px ui-sans-serif, system-ui, sans-serif`;

    // Adjust text for markdown processing
    let processedText = text;
    if (isMarkdown) {
      // Remove basic markdown formatting for measurement
      processedText = text
        .replace(/#{1,6}\s/g, '') // Remove headers
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\*(.*?)\*/g, '$1') // Remove italic
        .replace(/`(.*?)`/g, '$1') // Remove code
        .replace(/\[(.*?)\]\(.*?\)/g, '$1'); // Remove links
    }

    const lines = processedText.split('\n');
    const lineHeight = fontSize * 1.5; // Approximate line height
    let totalHeight = 0;
    let maxLineWidth = 0;

    for (const line of lines) {
      const words = line.split(/\s+/).filter(word => word.length > 0);
      let currentLineWidth = 0;
      let linesInParagraph = 1; // Start with 1 line, not 0

      if (words.length === 0) {
        // Empty line
        totalHeight += lineHeight;
        continue;
      }

      for (const word of words) {
        const wordWidth = context.measureText(word + ' ').width;

        if (currentLineWidth + wordWidth > maxWidth && currentLineWidth > 0) {
          // Word doesn't fit, start new line
          linesInParagraph++;
          currentLineWidth = wordWidth;
        } else {
          currentLineWidth += wordWidth;
        }

        maxLineWidth = Math.max(maxLineWidth, currentLineWidth);
      }

      // Add all lines in this paragraph to total height
      totalHeight += linesInParagraph * lineHeight;
    }

    // Ensure minimum height for empty or very short text
    if (totalHeight < lineHeight) {
      totalHeight = lineHeight;
    }

    const result = {
      width: Math.min(maxLineWidth, maxWidth),
      height: totalHeight
    };

    addToCache(cacheKey, result);
    return result;
  } catch (error) {
    console.warn('Error measuring text, using fallback:', error);
    return fallbackMeasurement(text, fontSize, maxWidth);
  }
}

/**
 * Fallback text measurement when canvas is not available
 */
function fallbackMeasurement(text: string, fontSize: FontSize, maxWidth: number): TextMeasurement {
  const averageCharWidth = fontSize * 0.6; // Rough estimate
  const lineHeight = fontSize * 1.5;
  const charsPerLine = Math.floor(maxWidth / averageCharWidth);
  const lineCount = Math.max(1, Math.ceil(text.length / charsPerLine));

  return {
    width: Math.min(text.length * averageCharWidth, maxWidth),
    height: lineCount * lineHeight
  };
}

/**
 * Calculates the optimal font size for given text and container dimensions
 */
function calculateOptimalFontSize(text: string, containerDimensions: BoxDimensions, isMarkdown: boolean = false): FontSize {
  // For empty text, use safe initial font size to prevent layout issues
  if (!text.trim()) {
    return FONT_SIZES[CALIBRATION.SAFE_INITIAL_INDEX];
  }

  if (containerDimensions.width <= 0 || containerDimensions.height <= 0) {
    return FONT_SIZES[0]; // Return smallest size if container is too small
  }

  // Account for padding/margins using calibration values
  const availableWidth = containerDimensions.width - CALIBRATION.WIDTH_PADDING;
  const availableHeight = containerDimensions.height - CALIBRATION.HEIGHT_PADDING;

  // Ensure minimum available space
  if (availableWidth <= 0 || availableHeight <= 0) {
    return FONT_SIZES[0]; // Return smallest size if container is too small
  }

  // Try font sizes from largest to smallest
  for (let i = FONT_SIZES.length - 1; i >= 0; i--) {
    const fontSize = FONT_SIZES[i];
    const measurement = measureText(text, fontSize, availableWidth, isMarkdown);

    // Apply font-size-specific scaling factor for more accurate calculations
    const scalingFactor = CALIBRATION.FONT_SIZE_FACTORS[i];
    const adjustedHeight = measurement.height * scalingFactor;

    // Add calibrated buffer space
    const heightWithBuffer = adjustedHeight * (1 + CALIBRATION.HEIGHT_BUFFER_PERCENT / 100);

    // Check if text fits within available space
    if (heightWithBuffer <= availableHeight) {
      return fontSize;
    }
  }

  // If nothing fits, return the smallest size
  return FONT_SIZES[0];
}/**
 * Custom hook for dynamic font sizing across input/output boxes
 */
export function useDynamicFontSize() {
  const [fontSize, setFontSize] = useState<FontSize>(FONT_SIZES[CALIBRATION.SAFE_INITIAL_INDEX]); // Start with safe size
  const inputTextRef = useRef<string>('');
  const outputTextRef = useRef<string>('');
  const inputDimensionsRef = useRef<BoxDimensions>({ width: 0, height: 0 });
  const outputDimensionsRef = useRef<BoxDimensions>({ width: 0, height: 0 });
  const isMarkdownEnabledRef = useRef<boolean>(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastCalculatedSizeRef = useRef<FontSize>(FONT_SIZES[CALIBRATION.SAFE_INITIAL_INDEX]);
  const isTypingRef = useRef<boolean>(false);

  // Immediate calculation function (no debounce)
  const calculateFontSizeImmediate = useCallback(() => {
    const inputOptimal = calculateOptimalFontSize(
      inputTextRef.current,
      inputDimensionsRef.current
    );

    const outputOptimal = calculateOptimalFontSize(
      outputTextRef.current,
      outputDimensionsRef.current,
      isMarkdownEnabledRef.current
    );

    // Use the smaller of the two optimal sizes to ensure consistency
    const newFontSize = Math.min(inputOptimal, outputOptimal) as FontSize;

    // Only update if the size actually changed to reduce re-renders
    if (newFontSize !== lastCalculatedSizeRef.current) {
      lastCalculatedSizeRef.current = newFontSize;
      setFontSize(newFontSize);
    }
  }, []);

  // Debounced calculation function (for resize events)
  const calculateFontSizeDebounced = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      calculateFontSizeImmediate();
    }, 100); // Reduced debounce time for more responsiveness
  }, [calculateFontSizeImmediate]);

  // Update input text (no automatic recalculation)
  const updateInputText = useCallback((text: string) => {
    inputTextRef.current = text;
  }, []);

  // Update output text (no automatic recalculation)
  const updateOutputText = useCallback((text: string) => {
    outputTextRef.current = text;
  }, []);

  // Update input dimensions and recalculate (debounced for resize)
  const updateInputDimensions = useCallback((dimensions: BoxDimensions) => {
    inputDimensionsRef.current = dimensions;
    // Skip calculations while user is actively typing
    if (!isTypingRef.current) {
      calculateFontSizeDebounced();
    }
  }, [calculateFontSizeDebounced]);

  // Update output dimensions and recalculate (debounced for resize)
  const updateOutputDimensions = useCallback((dimensions: BoxDimensions) => {
    outputDimensionsRef.current = dimensions;
    // Skip calculations while user is actively typing
    if (!isTypingRef.current) {
      calculateFontSizeDebounced();
    }
  }, [calculateFontSizeDebounced]);

  // Update markdown mode and recalculate immediately
  const updateMarkdownMode = useCallback((isMarkdown: boolean) => {
    isMarkdownEnabledRef.current = isMarkdown;
    calculateFontSizeImmediate();
  }, [calculateFontSizeImmediate]);

  // Pre-calculate font size for a given text without applying it
  const preCalculateFontSize = useCallback((inputText: string, outputText: string): FontSize => {
    const inputOptimal = calculateOptimalFontSize(
      inputText,
      inputDimensionsRef.current
    );

    const outputOptimal = calculateOptimalFontSize(
      outputText,
      outputDimensionsRef.current,
      isMarkdownEnabledRef.current
    );

    return Math.min(inputOptimal, outputOptimal) as FontSize;
  }, []);

  // Set text with pre-calculated font size to prevent jumpiness
  const setTextWithOptimalFont = useCallback((inputText?: string, outputText?: string) => {
    const newInputText = inputText !== undefined ? inputText : inputTextRef.current;
    const newOutputText = outputText !== undefined ? outputText : outputTextRef.current;

    // Pre-calculate the optimal font size
    const optimalFontSize = preCalculateFontSize(newInputText, newOutputText);

    // Apply font size first
    setFontSize(optimalFontSize);

    // Then update the text refs
    if (inputText !== undefined) {
      inputTextRef.current = inputText;
    }
    if (outputText !== undefined) {
      outputTextRef.current = outputText;
    }
  }, [preCalculateFontSize]);

  // Create ResizeObserver instances for containers
  const createResizeObserver = useCallback((
    updateDimensions: (dimensions: BoxDimensions) => void
  ) => {
    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        updateDimensions({ width, height });
      }
    });
  }, []);

  // Clear measurement cache (useful for debugging)
  const clearCache = useCallback(() => {
    measurementCache.clear();
  }, []);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return {
    fontSize,
    updateInputText,
    updateOutputText,
    updateInputDimensions,
    updateOutputDimensions,
    updateMarkdownMode,
    preCalculateFontSize,
    setTextWithOptimalFont,
    createResizeObserver,
    clearCache,
    calculate: calculateFontSizeImmediate, // Explicit calculate method
    setTyping: (typing: boolean) => { isTypingRef.current = typing; },
    // Expose calibration values for debugging/tuning
    calibration: CALIBRATION,
    fontSizes: FONT_SIZES,
  };
}
